/**
 * API utility functions for connecting to Django backend
 */

// Base URL for Django backend.
// IMPORTANT:
// - In production, set NEXT_PUBLIC_API_URL to your Django backend origin (e.g. https://zamleaders.com)
// - In local dev, if unset, we default to http://localhost:8001
const API_BASE_URL = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001').replace(/\/$/, '');

interface ApiResponse<T = any> {
  data?: T;
  detail?: string;
  success?: boolean;
  message?: string;
  errors?: Record<string, string[]>;
}

class ApiError extends Error {
  status: number;
  errors?: Record<string, string[]>;

  constructor(message: string, status: number, errors?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
  }
}

/**
 * Get auth token from cookie
 */
function getAuthToken(): string | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'auth_token') {
      return value;
    }
  }
  return null;
}

/**
 * Base fetch function with authentication
 */
async function apiFetch<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Add cache-busting for GET requests to ensure fresh data
  let finalEndpoint = endpoint;
  if (!options.method || options.method === 'GET') {
    const separator = endpoint.includes('?') ? '&' : '?';
    finalEndpoint = `${endpoint}${separator}_t=${Date.now()}`;
  }
  const url = `${API_BASE_URL}${finalEndpoint}`;
  const token = getAuthToken();

  // Helpful guard: if running in a non-local browser session with the default localhost backend,
  // requests will fail. Surface a clear configuration error instead of a generic "Failed to fetch".
  if (
    typeof window !== 'undefined' &&
    (!process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_URL.trim() === '') &&
    (API_BASE_URL === 'http://localhost:8001' || API_BASE_URL === 'http://127.0.0.1:8001') &&
    window.location.hostname !== 'localhost' &&
    window.location.hostname !== '127.0.0.1'
  ) {
    throw new ApiError(
      'Frontend is not configured to reach the backend. Set NEXT_PUBLIC_API_URL to your Django backend URL.',
      0
    );
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Token is in HttpOnly cookie (auth_token), automatically sent with credentials: 'include'
  // No need to add Authorization header - CookieTokenAuthentication reads from cookie

  // Handle FormData (for file uploads)
  if (options.body instanceof FormData) {
    // Remove Content-Type header for FormData (browser will set it with boundary)
    const headersObj = headers as Record<string, string>;
    delete headersObj['Content-Type'];
  }

  const config: RequestInit = {
    ...options,
    headers,
    credentials: 'include', // CRITICAL: Sends auth_token cookie with request
  };

  try {
    const response = await fetch(url, config);
    
    // Check if response has content
    const contentType = response.headers.get('content-type');
    const text = await response.text();
    
    if (!response.ok) {
      let errorData: any = {};
      try {
        errorData = text ? JSON.parse(text) : {};
      } catch (e) {
        // If response is not JSON, use text as error message
        throw new ApiError(
          text || 'An error occurred',
          response.status
        );
      }
      
      // Extract error message from standard response format
      // Standard format: {success: false, message: "...", errors: {...}, data: []}
      let errorMessage = 'An error occurred';
      let validationErrors: Record<string, string[]> = {};
      
      // Check for standard response format first
      if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
        // Standard format: {success: false, message: "...", errors: {...}}
        if ('message' in errorData && typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
        
        // Extract validation errors from standard format
        if (errorData.errors && typeof errorData.errors === 'object' && !Array.isArray(errorData.errors)) {
          // Convert error arrays to strings for display
          for (const [key, value] of Object.entries(errorData.errors)) {
            if (Array.isArray(value)) {
              validationErrors[key] = value.map(v => String(v));
            } else if (value !== null && value !== undefined) {
              validationErrors[key] = [String(value)];
            }
          }
        }
      }
      
      // If we got a standard response with message, use it and return early if we have errors
      if (errorData && typeof errorData === 'object' && !Array.isArray(errorData) && 'message' in errorData && errorData.errors) {
        throw new ApiError(
          errorMessage,
          response.status,
          Object.keys(validationErrors).length > 0 ? validationErrors : undefined
        );
      }
      
      // Fallback: Check for Django REST Framework validation errors (legacy format)
      // Django can return errors in multiple formats:
      // 1. Wrapped in "errors" key: {errors: {field: ["error"]}}
      // 2. At top level: {field: ["error"]} - THIS IS THE MOST COMMON FORMAT
      // 3. Array format: ["error message"] - handle this case
      let errorsToProcess: any = null;
      
      // Handle array responses (like ["Profile can only be edited..."])
      if (Array.isArray(errorData) && errorData.length > 0) {
        // Convert array to standard format
        errorMessage = String(errorData[0]);
        throw new ApiError(
          errorMessage,
          response.status,
          undefined
        );
      }
      
      // First, check for explicit errors key
      if (errorData.errors && typeof errorData.errors === 'object' && !Array.isArray(errorData.errors)) {
        errorsToProcess = errorData.errors;
      } else if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
        // Check if errorData itself contains field errors (Django REST Framework format)
        // This is the most common case - Django returns {field_name: ["error message"]}
        const fieldErrorKeys: string[] = [];
        const nonFieldKeys = ['detail', 'message', 'error', 'success', 'status', 'code'];
        
        for (const [key, value] of Object.entries(errorData)) {
          // Skip common non-field keys
          if (nonFieldKeys.includes(key.toLowerCase())) {
            continue;
          }
          
          // Check if value looks like a validation error
          if (Array.isArray(value)) {
            // Array of error messages (most common Django format)
            if (value.length > 0) {
              // Check if it contains strings or ErrorDetail objects
              const hasErrors = value.some(v => {
                if (typeof v === 'string') return true;
                if (typeof v === 'object' && v !== null) {
                  // Django ErrorDetail objects have a string representation
                  return true;
                }
                return false;
              });
              if (hasErrors) {
                fieldErrorKeys.push(key);
              }
            }
          } else if (typeof value === 'string' && value.length > 0) {
            // Single string error message
            fieldErrorKeys.push(key);
          } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            // Nested error object
            fieldErrorKeys.push(key);
          }
        }
        
        // If we found field-like errors, use them
        if (fieldErrorKeys.length > 0) {
          errorsToProcess = {};
          for (const key of fieldErrorKeys) {
            errorsToProcess[key] = errorData[key];
          }
        }
      }
      
      if (errorsToProcess) {
        // Convert error arrays to strings for display
        validationErrors = {};
        for (const [key, value] of Object.entries(errorsToProcess)) {
          if (Array.isArray(value)) {
            // Handle Django ErrorDetail objects - they stringify to their message
            validationErrors[key] = value.map(v => {
              if (typeof v === 'string') {
                return v;
              } else if (typeof v === 'object' && v !== null) {
                // Django ErrorDetail object - try to extract the message
                if ('string' in v) {
                  return String(v.string);
                } else if ('message' in v) {
                  return String(v.message);
                } else {
                  return String(v);
                }
              } else {
                return String(v);
              }
            });
          } else if (value !== null && value !== undefined) {
            validationErrors[key] = [String(value)];
          }
        }
        
        // Create a user-friendly error message from validation errors
        // ALWAYS list each field individually - never say "all fields required"
        if (Object.keys(validationErrors).length > 0) {
          const errorList = Object.entries(validationErrors)
            .map(([field, messages]) => {
              // Convert field names to user-friendly labels
              const fieldLabels: Record<string, string> = {
                'full_name': 'Full Name',
                'leadership_type': 'Leadership Type',
                'political_party': 'Political Party',
                'constituency': 'Constituency',
                'province': 'Province',
                'district': 'District',
                'ward': 'Ward',
                'email': 'Email',
                'phone': 'Phone',
                'biography': 'Biography',
                'electoral_cycle': 'Electoral Cycle',
                'party_position': 'Party Position',
                'party_position_level': 'Party Position Level',
                'party_position_other': 'Party Position Other',
                'status': 'Status',
                'permission': 'Permission',
              };
              
              const fieldLabel = fieldLabels[field] || field
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase());
              
              // Join all error messages for this field
              const allMessages = messages.join(', ');
              return `${fieldLabel}: ${allMessages}`;
            });
          
          if (errorList.length > 0) {
            // Show first error in toast, all errors in banner
            errorMessage = errorList[0];
          }
        }
      } else if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
        // Try to get message from standard format or legacy format
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      }
      
      // On 401, notify all listeners (PublicHeader, dashboard pages) so they re-check auth and redirect
      if (response.status === 401 && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authStateChanged'));
      }
      // Provide fallback error messages based on status code (only if we still don't have a good message)
      if (errorMessage === 'An error occurred' || !errorMessage) {
        switch (response.status) {
          case 400:
            errorMessage = 'Invalid request. Please check your input.';
            break;
          case 401:
            errorMessage = 'You are not authenticated. Please sign in and try again.';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            break;
          case 404:
            errorMessage = 'The requested resource was not found.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = `An error occurred (${response.status}). Please try again.`;
        }
      }
      
      throw new ApiError(
        errorMessage,
        response.status,
        Object.keys(validationErrors).length > 0 ? validationErrors : undefined
      );
    }

    // Handle empty responses
    if (!text || text.trim() === '') {
      return null as T;
    }

    // Try to parse JSON
    let data: any;
    try {
      data = JSON.parse(text);
    } catch (e) {
      const trimmed = (text || '').trim();
      // Common misconfig: calling Next.js (HTML) instead of Django (JSON)
      if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
        throw new ApiError(
          `Invalid API response (received HTML). Check NEXT_PUBLIC_API_URL (currently: ${API_BASE_URL}).`,
          response.status
        );
      }
      // If not JSON, return null
      console.warn(`Response from ${url} is not valid JSON:`, text);
      return null as T;
    }

    // Handle different response formats
    // If response has a 'data' field, use it; otherwise use the response directly
    // Also handle empty objects/null responses
    if (data && typeof data === 'object') {
      if ('data' in data && data.data !== undefined) {
        // Handle both arrays and objects in data field
        return data.data as T;
      }
      // If it's an empty object or null, return null
      if (Object.keys(data).length === 0 || data === null) {
        return null as T;
      }
      // If it's an array, return it directly
      if (Array.isArray(data)) {
        return data as T;
      }
      // Otherwise return the data directly
      return data as T;
    }
    
    // If data is an array, return it
    if (Array.isArray(data)) {
      return data as T;
    }
    
    return data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error',
      0
    );
  }
}

/**
 * Authentication API
 */
export const userApi = {
  /**
   * Get current user profile
   */
  getProfile: async () => {
    return apiFetch('/api/user/profile/');
  },

  /**
   * Update current user profile
   */
  updateProfile: async (data: FormData | Record<string, any>) => {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    const headers: HeadersInit = data instanceof FormData 
      ? {} 
      : { 'Content-Type': 'application/json' };
    
    return apiFetch('/api/user/profile/', {
      method: 'PUT',
      body,
      headers,
    });
  },
};

export const authApi = {
  /**
   * Sign up a new user
   */
  signup: async (data: {
    username: string;
    email: string;
    password: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
  }) => {
    // Signup should include credentials to receive session cookie from Django
    return apiFetch('/api/auth/signup/', {
      method: 'POST',
      body: JSON.stringify(data),
      credentials: 'include', // Ensure cookies are included
    });
  },

  /**
   * Sign in a user
   */
  login: async (username: string, password: string) => {
    return apiFetch<{
      user_id: number;
      email: string;
      username: string;
      role: string;
      is_staff: boolean;
      is_superuser: boolean;
      authenticated: boolean;
    }>('/api/auth/login/', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },

  /**
   * Check authentication status
   * @param sessionType - 'admin' to check admin session, 'user' to check user session, or null/undefined to auto-detect from path
   */
  checkAuth: async (sessionType?: 'admin' | 'user' | null) => {
    const endpoint = sessionType 
      ? `/api/auth/check/?session_type=${sessionType}`
      : '/api/auth/check/';
    return apiFetch<{
      authenticated: boolean;
      user_id?: number;
      email?: string;
      username?: string;
      first_name?: string;
      last_name?: string;
      phone?: string;
      role?: string;
      is_staff?: boolean;
      is_superuser?: boolean;
      session_type?: 'admin' | 'user' | null;
      profile_photo_url?: string | null;
    }>(endpoint);
  },

  /**
   * Logout
   */
  logout: async () => {
    return apiFetch('/api/auth/logout/', {
      method: 'POST',
    });
  },

  /**
   * Update user role
   */
  updateRole: async (role: 'leader' | 'pa' | 'agent') => {
    return apiFetch('/api/auth/update-role/', {
      method: 'POST',
      body: JSON.stringify({ role }),
    });
  },
};

/**
 * Leader Profiles API
 */
export const leadersApi = {
  /**
   * Get list of leaders
   */
  list: async (params?: {
    show_own?: boolean;
    leadership_type?: string;
    electoral_cycle?: string;
    political_party?: string;
    province?: string;
    district?: string;
    search?: string;
    is_former?: string | boolean;
    include_all?: boolean; // Fetch all profiles regardless of status (for validation)
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }
    const query = queryParams.toString();
    const result = await apiFetch<any[]>(`/api/leaders/${query ? `?${query}` : ''}`);
    // Always return an array, never null
    return Array.isArray(result) ? result : (result === null || result === undefined ? [] : [result]);
  },

  /**
   * Get leader by ID or slug
   */
  get: async (idOrSlug: number | string) => {
    if (typeof idOrSlug === 'string' && !/^\d+$/.test(idOrSlug)) {
      // It's a slug
      return apiFetch(`/api/leaders/by-slug/${idOrSlug}/`);
    }
    // It's an ID
    return apiFetch(`/api/leaders/${idOrSlug}/`);
  },

  /**
   * Transfer ownership of a leader profile from PA/agent to the leader.
   * Callable only by the PA or agent assigned to the profile. Sets the leader's account password and makes the profile leader-owned.
   */
  transferOwnership: async (leaderProfileId: number, data: { password: string }) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/transfer-ownership/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get contact information for a leader profile
   */
  getContact: async (leaderProfileId: number | string) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/contact/`);
  },

  /**
   * Update contact information for a leader profile
   */
  updateContact: async (leaderProfileId: number | string, data: {
    office_address?: string;
    working_hours?: string;
    facebook_url?: string;
    twitter_url?: string;
    instagram_url?: string;
    linkedin_url?: string;
    website_url?: string;
  }) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/contact/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Achievements (Key Achievements)
   */
  getAchievements: async (leaderProfileId: number | string) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/achievements/`);
  },

  createAchievement: async (
    leaderProfileId: number | string,
    data: { title: string; description?: string; date?: string; category?: string }
  ) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/achievements/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateAchievement: async (
    leaderProfileId: number | string,
    achievementId: number,
    data: Partial<{ title: string; description?: string; date?: string; category?: string }>
  ) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/achievements/${achievementId}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteAchievement: async (leaderProfileId: number | string, achievementId: number) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/achievements/${achievementId}/`, {
      method: 'DELETE',
    });
  },

  /**
   * Key Responsibilities
   */
  getResponsibilities: async (leaderProfileId: number | string) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/responsibilities/`);
  },

  createResponsibility: async (
    leaderProfileId: number | string,
    data: { title: string; description?: string; display_order?: number }
  ) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/responsibilities/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateResponsibility: async (
    leaderProfileId: number | string,
    responsibilityId: number,
    data: Partial<{ title: string; description?: string; display_order?: number }>
  ) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/responsibilities/${responsibilityId}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteResponsibility: async (leaderProfileId: number | string, responsibilityId: number) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/responsibilities/${responsibilityId}/`, {
      method: 'DELETE',
    });
  },

  /**
   * Create leader profile
   */
  create: async (data: FormData) => {
    // Handle committee_memberships if it's a JSON string
    const committeeMemberships = data.get('committee_memberships');
    if (committeeMemberships && typeof committeeMemberships === 'string') {
      try {
        const committees = JSON.parse(committeeMemberships);
        data.delete('committee_memberships');
        data.append('committee_memberships', JSON.stringify(committees));
      } catch (e) {
        // If parsing fails, keep as is
      }
    }
    
    return apiFetch('/api/leaders/', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Update leader profile
   */
  update: async (id: number, data: FormData) => {
    // Handle committee_memberships if it's a JSON string
    const committeeMemberships = data.get('committee_memberships');
    if (committeeMemberships && typeof committeeMemberships === 'string') {
      try {
        const committees = JSON.parse(committeeMemberships);
        data.delete('committee_memberships');
        data.append('committee_memberships', JSON.stringify(committees));
      } catch (e) {
        // If parsing fails, keep as is
      }
    }
    
    return apiFetch(`/api/leaders/${id}/`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Submit profile for review
   */
  submit: async (id: number) => {
    return apiFetch(`/api/leaders/${id}/submit/`, {
      method: 'POST',
    });
  },

  /**
   * Request to edit an approved profile (leader/owner only). Sends request to admin.
   */
  requestEdit: async (profileId: number | string, reason: string) => {
    return apiFetch(`/api/leaders/${profileId}/request-edit/`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason.trim() }),
    });
  },

  /**
   * Get electoral cycles
   */
  getElectoralCycles: async () => {
    return apiFetch('/api/electoral-cycles/');
  },

  /**
   * Get political parties
   */
  getPoliticalParties: async () => {
    return apiFetch('/api/political-parties/');
  },

  /**
   * Get party positions
   */
  getPartyPositions: async () => {
    return apiFetch('/api/party-positions/');
  },

  /**
   * Get leadership types
   */
  getLeadershipTypes: async () => {
    return apiFetch('/api/leadership-types/');
  },
  getLeadershipPositions: async () => {
    // Fetch leadership positions from Django (public endpoint)
    return apiFetch('/api/leadership-positions/');
  },

  /**
   * Subscribe to a leader profile's updates
   */
  subscribe: async (leaderProfileId: number | string, email: string) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/subscribe/`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Unsubscribe from a leader profile's updates
   */
  unsubscribe: async (leaderProfileId: number | string, email: string) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/unsubscribe/`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },
};

/**
 * Q&A API
 */
export const qaApi = {
  /**
   * Get questions for a leader profile
   */
  getQuestions: async (leaderProfileId: number) => {
    return apiFetch(`/api/questions/?leader_profile=${leaderProfileId}`);
  },

  /**
   * Get question by ID
   */
  getQuestion: async (id: number) => {
    return apiFetch(`/api/questions/${id}/`);
  },

  /**
   * Create a new question
   */
  createQuestion: async (data: { leader_profile?: number; to_leader_profile?: number; question_text: string }) => {
    return apiFetch('/api/questions/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a question
   */
  deleteQuestion: async (id: number) => {
    return apiFetch(`/api/questions/${id}/`, {
      method: 'DELETE',
    });
  },

  /**
   * Create an answer to a question
   */
  createAnswer: async (questionId: number, answerText: string, leaderProfileId?: number) => {
    const body: any = { answer_text: answerText };
    if (leaderProfileId) {
      body.leader_profile = leaderProfileId;
    }
    return apiFetch(`/api/questions/${questionId}/answer/`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Update an answer
   */
  updateAnswer: async (answerId: number, answerText: string) => {
    return apiFetch(`/api/answers/${answerId}/`, {
      method: 'PUT',
      body: JSON.stringify({ answer_text: answerText }),
    });
  },
  listAllQuestions: async () => {
    console.log('=== qaApi.listAllQuestions() called ===');
    console.log('API_BASE_URL:', API_BASE_URL);
    console.log('Full URL:', `${API_BASE_URL}/api/questions/?all=true`);
    try {
      const result = await apiFetch('/api/questions/?all=true');
      console.log('=== qaApi.listAllQuestions() response ===');
      console.log('Result:', result);
      console.log('Result type:', typeof result);
      console.log('Is array:', Array.isArray(result));
      return result;
    } catch (error) {
      console.error('=== qaApi.listAllQuestions() error ===');
      console.error('Error:', error);
      throw error;
    }
  },
};

/**
 * Work API (Bills, Speeches, Projects)
 */
export const workApi = {
  /**
   * Get bills for a leader profile
   */
  getBills: async (leaderProfileId: number) => {
    return apiFetch(`/api/work/bills/?leader_profile=${leaderProfileId}`);
  },

  /**
   * Get a specific bill
   */
  getBill: async (id: number) => {
    return apiFetch(`/api/work/bills/${id}/`);
  },

  /**
   * Create a new bill
   */
  createBill: async (data: FormData) => {
    return apiFetch('/api/work/bills/', {
      method: 'POST',
      body: data, // FormData for file uploads
    });
  },

  /**
   * Update a bill
   */
  updateBill: async (id: number, data: FormData) => {
    return apiFetch(`/api/work/bills/${id}/`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Delete a bill
   */
  deleteBill: async (id: number) => {
    return apiFetch(`/api/work/bills/${id}/`, {
      method: 'DELETE',
    });
  },

  /**
   * Get speeches for a leader profile
   */
  getSpeeches: async (leaderProfileId: number) => {
    return apiFetch(`/api/work/speeches/?leader_profile=${leaderProfileId}`);
  },

  /**
   * Get a specific speech
   */
  getSpeech: async (id: number) => {
    return apiFetch(`/api/work/speeches/${id}/`);
  },

  /**
   * Create a new speech
   */
  createSpeech: async (data: FormData) => {
    return apiFetch('/api/work/speeches/', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Update a speech
   */
  updateSpeech: async (id: number, data: FormData) => {
    return apiFetch(`/api/work/speeches/${id}/`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Delete a speech
   */
  deleteSpeech: async (id: number) => {
    return apiFetch(`/api/work/speeches/${id}/`, {
      method: 'DELETE',
    });
  },

  /**
   * Get projects for a leader profile
   */
  getProjects: async (leaderProfileId: number) => {
    return apiFetch(`/api/work/projects/?leader_profile=${leaderProfileId}`);
  },

  /**
   * Get a specific project
   */
  getProject: async (id: number) => {
    return apiFetch(`/api/work/projects/${id}/`);
  },

  /**
   * Create a new project
   */
  createProject: async (data: FormData) => {
    return apiFetch('/api/work/projects/', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Update a project
   */
  updateProject: async (id: number, data: FormData) => {
    return apiFetch(`/api/work/projects/${id}/`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Delete a project
   */
  deleteProject: async (id: number) => {
    return apiFetch(`/api/work/projects/${id}/`, {
      method: 'DELETE',
    });
  },

  /**
   * Get motions for a leader profile
   */
  getMotions: async (leaderProfileId: number) => {
    return apiFetch(`/api/work/motions/?leader_profile=${leaderProfileId}`);
  },

  /**
   * Get a specific motion
   */
  getMotion: async (id: number) => {
    return apiFetch(`/api/work/motions/${id}/`);
  },

  /**
   * Create a new motion
   */
  createMotion: async (data: FormData) => {
    return apiFetch('/api/work/motions/', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Update a motion
   */
  updateMotion: async (id: number, data: FormData) => {
    return apiFetch(`/api/work/motions/${id}/`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Delete a motion
   */
  deleteMotion: async (id: number) => {
    return apiFetch(`/api/work/motions/${id}/`, {
      method: 'DELETE',
    });
  },
};

/**
 * Activities API
 */
export const activitiesApi = {
  /**
   * Get activities for a leader profile
   */
  list: async (leaderProfileId: number) => {
    return apiFetch(`/api/activities/?leader_profile=${leaderProfileId}`);
  },

  /**
   * Get all published activities (for public profile pages)
   */
  /**
   * List all published activities (public feed)
   * @param limit - Number of activities to return
   * @param offset - Number of activities to skip
   */
  listAllPublished: async (limit?: number, offset?: number) => {
    const params = new URLSearchParams();
    params.append('all_published', 'true');
    if (limit !== undefined) params.append('limit', String(limit));
    if (offset !== undefined) params.append('offset', String(offset));
    return apiFetch(`/api/activities/?${params.toString()}`);
  },

  /**
   * Get activity by ID
   */
  get: async (id: number) => {
    return apiFetch(`/api/activities/${id}/`);
  },

  /**
   * Create a new activity
   */
  create: async (data: FormData) => {
    return apiFetch('/api/activities/', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Update an activity
   */
  update: async (id: number, data: FormData) => {
    return apiFetch(`/api/activities/${id}/`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Delete an activity
   */
  delete: async (id: number) => {
    return apiFetch(`/api/activities/${id}/`, {
      method: 'DELETE',
    });
  },

  /**
   * Get activity types
   */
  getTypes: async () => {
    return apiFetch('/api/leaders/activity-types/');
  },

  /**
   * Get activity categories
   */
  getCategories: async () => {
    return apiFetch('/api/leaders/activity-categories/');
  },
};

/**
 * Posts interactions API (likes/comments/shares)
 * Note: "posts" are Activity records whose ActivityType code is one of: text, media, announcement.
 */
export const postsApi = {
  toggleLike: async (postId: number) => {
    return apiFetch<{ liked: boolean; like_count: number }>(`/api/posts/${postId}/like/`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  listComments: async (postId: number) => {
    return apiFetch<{ comments: any[]; comment_count: number }>(`/api/posts/${postId}/comments/`);
  },

  createComment: async (
    postId: number,
    text: string,
    parentCommentId?: number,
    mentionProfileIds?: number[],
    imageFile?: File | null
  ) => {
    const actorProfileIdRaw =
      typeof window !== 'undefined' ? sessionStorage.getItem('zamLeaders_selected_profile_id') : null;
    const actorProfileId = actorProfileIdRaw ? Number(actorProfileIdRaw) : null;

    // If there is an image, use FormData; otherwise JSON is fine.
    if (imageFile) {
      const fd = new FormData();
      fd.append('text', text || '');
      if (parentCommentId) fd.append('parent_comment_id', String(parentCommentId));
      if (actorProfileId && !Number.isNaN(actorProfileId)) fd.append('actor_profile_id', String(actorProfileId));
      if (Array.isArray(mentionProfileIds) && mentionProfileIds.length > 0) {
        // backend accepts getlist() as well as JSON strings; send repeated fields for multipart
        for (const id of mentionProfileIds) fd.append('mention_profile_ids', String(id));
      }
      fd.append('image', imageFile);
      return apiFetch<{ comment: any; comment_count: number }>(`/api/posts/${postId}/comments/`, {
        method: 'POST',
        body: fd,
      });
    }

    return apiFetch<{ comment: any; comment_count: number }>(`/api/posts/${postId}/comments/`, {
      method: 'POST',
      body: JSON.stringify({
        text,
        parent_comment_id: parentCommentId,
        actor_profile_id: actorProfileId && !Number.isNaN(actorProfileId) ? actorProfileId : undefined,
        mention_profile_ids: mentionProfileIds || [],
      }),
    });
  },

  updateComment: async (
    postId: number,
    commentId: number,
    text: string,
    mentionProfileIds?: number[],
    imageFile?: File | null,
    clearImage?: boolean
  ) => {
    // PUT supports multipart so we can upload/clear images.
    const fd = new FormData();
    fd.append('text', text || '');
    if (Array.isArray(mentionProfileIds) && mentionProfileIds.length > 0) {
      for (const id of mentionProfileIds) fd.append('mention_profile_ids', String(id));
    }
    if (typeof clearImage !== 'undefined') fd.append('clear_image', clearImage ? 'true' : 'false');
    if (imageFile) fd.append('image', imageFile);
    return apiFetch<{ comment: any; comment_count: number }>(`/api/posts/${postId}/comments/${commentId}/`, {
      method: 'PUT',
      body: fd,
    });

  },

  deleteComment: async (postId: number, commentId: number) => {
    return apiFetch<{ success: boolean; comment_count: number }>(`/api/posts/${postId}/comments/${commentId}/`, {
      method: 'DELETE',
    });
  },
  toggleCommentLike: async (postId: number, commentId: number) => {
    return apiFetch<{ liked: boolean; like_count: number }>(`/api/posts/${postId}/comments/${commentId}/like/`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },


  share: async (postId: number, channel?: string) => {
    return apiFetch<{ success: boolean; share_count: number }>(`/api/posts/${postId}/share/`, {
      method: 'POST',
      body: JSON.stringify(channel ? { channel } : {}),
    });
  },
};

/**
 * Administrative Units API
 */
export const adminUnitsApi = {
  /**
   * Get provinces
   */
  getProvinces: async () => {
    return apiFetch('/api/administrative-units/provinces/');
  },

  /**
   * Get districts
   */
  getDistricts: async (provinceId?: number) => {
    const query = provinceId ? `?province_id=${provinceId}` : '';
    return apiFetch(`/api/administrative-units/districts${query}`);
  },

  /**
   * Get constituencies
   */
  getConstituencies: async (districtId?: number) => {
    const query = districtId ? `?district_id=${districtId}` : '';
    return apiFetch(`/api/administrative-units/constituencies${query}`);
  },

  /**
   * Get wards
   */
  getWards: async (constituencyId?: number, districtId?: number) => {
    const params = new URLSearchParams();
    if (constituencyId) params.append('constituency_id', String(constituencyId));
    if (districtId) params.append('district_id', String(districtId));
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiFetch(`/api/administrative-units/wards${query}`);
  },

  /**
   * Get complete tree
   */
  getTree: async () => {
    return apiFetch('/api/administrative-units/tree/');
  },
};

/**
 * Committees API
 */
export const committeesApi = {
  /**
   * Get list of committees with optional search
   */
  list: async (search?: string) => {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return apiFetch(`/api/committees${query}`);
  },
};

/**
 * Electoral Cycles API
 */
export const electoralCyclesApi = {
  /**
   * Get list of electoral cycles
   */
  list: async () => {
    return apiFetch('/api/electoral-cycles/');
  },
};

/**
 * Political Parties API
 */
export const politicalPartiesApi = {
  /**
   * Get list of political parties
   */
  list: async () => {
    return apiFetch('/api/political-parties/');
  },
};

/**
 * Admin API
 */
export const adminApi = {
  /**
   * Get admin dashboard stats
   */
  getDashboard: async () => {
    return apiFetch('/api/admin/dashboard/');
  },

  /**
   * Get activity logs
   */
  getActivityLogs: async (params?: {
    model_name?: string;
    action?: string;
    days?: number;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }
    const query = queryParams.toString();
    return apiFetch(`/api/activity-logs/${query ? `?${query}` : ''}`);
  },

  /**
   * Get all leaders (admin)
   */
  getLeaders: async (params?: {
    status?: string;
    leadership_type?: string;
    electoral_cycle?: string;
    search?: string;
    is_former?: string;
    page?: number;
    page_size?: number;
    submitted_after?: string;
    submitted_before?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }
    const query = queryParams.toString();
    return apiFetch(`/api/admin/leaders/${query ? `?${query}` : ''}`);
  },

  /**
   * Get review queue
   */
  getReviews: async () => {
    return apiFetch('/api/admin/reviews/');
  },

  /**
   * Approve profile
   */
  approveProfile: async (id: number) => {
    return apiFetch(`/api/admin/leaders/${id}/approve/`, {
      method: 'POST',
    });
  },

  /**
   * Reject profile
   */
  rejectProfile: async (id: number, reason: string) => {
    return apiFetch(`/api/admin/leaders/${id}/reject/`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  /**
   * Edit profile (admin)
   */
  editProfile: async (id: number, data: FormData) => {
    return apiFetch(`/api/admin/leaders/${id}/edit/`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Mark leader as former/current (admin)
   */
  markFormerLeader: async (id: number, isFormer: boolean, termEndDate?: string) => {
    return apiFetch(`/api/admin/leaders/${id}/mark-former/`, {
      method: 'POST',
      body: JSON.stringify({
        is_former: isFormer,
        term_end_date: termEndDate || null,
      }),
    });
  },

  /**
   * List profile edit requests (admin)
   */
  getProfileEditRequests: async (params?: { status?: string }) => {
    const url = params?.status ? `/api/admin/profile-edit-requests/?status=${encodeURIComponent(params.status)}` : '/api/admin/profile-edit-requests/';
    return apiFetch(url);
  },

  /**
   * Mark profile edit request as closed (admin)
   */
  closeProfileEditRequest: async (id: number) => {
    return apiFetch(`/api/admin/profile-edit-requests/${id}/close/`, {
      method: 'PATCH',
    });
  },

  /**
   * Grant edit request: set profile to draft and notify leader (admin)
   */
  grantProfileEditRequest: async (id: number) => {
    return apiFetch(`/api/admin/profile-edit-requests/${id}/grant/`, {
      method: 'POST',
    });
  },

  /**
   * Reject edit request with reason: set profile to rejected and notify (admin)
   */
  rejectProfileEditRequest: async (id: number, reason: string) => {
    return apiFetch(`/api/admin/profile-edit-requests/${id}/reject/`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason.trim() }),
    });
  },

  /**
   * Get single leader profile (admin)
   */
  getLeader: async (id: number) => {
    return apiFetch(`/api/admin/leaders/${id}/`);
  },

  // Committees
  getCommittees: async () => {
    return apiFetch('/api/admin/committees/');
  },
  createCommittee: async (data: { name: string; description?: string; is_active?: boolean }) => {
    return apiFetch('/api/admin/committees/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  updateCommittee: async (id: number, data: { name?: string; description?: string; is_active?: boolean }) => {
    return apiFetch(`/api/admin/committees/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteCommittee: async (id: number) => {
    return apiFetch(`/api/admin/committees/${id}/`, {
      method: 'DELETE',
    });
  },

  // Political Parties
  getPoliticalParties: async () => {
    return apiFetch('/api/admin/political-parties/');
  },
  createPoliticalParty: async (data: FormData) => {
    return apiFetch('/api/admin/political-parties/', {
      method: 'POST',
      body: data,
    });
  },
  updatePoliticalParty: async (id: number, data: FormData) => {
    return apiFetch(`/api/admin/political-parties/${id}/`, {
      method: 'PUT',
      body: data,
    });
  },
  deletePoliticalParty: async (id: number) => {
    return apiFetch(`/api/admin/political-parties/${id}/`, {
      method: 'DELETE',
    });
  },

  // Party Positions
  getPartyPositions: async () => {
    return apiFetch('/api/admin/party-positions/');
  },
  createPartyPosition: async (data: { name: string; code: string; level: string; display_order?: number; is_active?: boolean }) => {
    return apiFetch('/api/admin/party-positions/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  updatePartyPosition: async (id: number, data: { name?: string; code?: string; level?: string; display_order?: number; is_active?: boolean }) => {
    return apiFetch(`/api/admin/party-positions/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deletePartyPosition: async (id: number) => {
    return apiFetch(`/api/admin/party-positions/${id}/`, {
      method: 'DELETE',
    });
  },

  // Parliamentary Positions
  getParliamentaryPositions: async () => {
    return apiFetch('/api/admin/parliamentary-positions/');
  },
  createParliamentaryPosition: async (data: { position: string; name: string; description?: string; is_active?: boolean }) => {
    return apiFetch('/api/admin/parliamentary-positions/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  updateParliamentaryPosition: async (id: number, data: { position?: string; name?: string; description?: string; is_active?: boolean }) => {
    return apiFetch(`/api/admin/parliamentary-positions/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteParliamentaryPosition: async (id: number) => {
    return apiFetch(`/api/admin/parliamentary-positions/${id}/`, {
      method: 'DELETE',
    });
  },
  getParliamentaryPositionAssignments: async (electoral_cycle?: number) => {
    const query = electoral_cycle ? `?electoral_cycle=${electoral_cycle}` : '';
    return apiFetch(`/api/admin/parliamentary-positions/assignments/${query}`);
  },
  createParliamentaryPositionAssignment: async (data: { position: number; leader_profile: number; electoral_cycle: number }) => {
    return apiFetch('/api/admin/parliamentary-positions/assignments/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  updateParliamentaryPositionAssignment: async (id: number, data: { position?: number; leader_profile?: number; electoral_cycle?: number }) => {
    return apiFetch(`/api/admin/parliamentary-positions/assignments/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteParliamentaryPositionAssignment: async (id: number) => {
    return apiFetch(`/api/admin/parliamentary-positions/assignments/${id}/`, {
      method: 'DELETE',
    });
  },
  getApprovedLeaders: async () => {
    return apiFetch('/api/admin/approved-leaders/');
  },

  // Administrative Units
  getProvinces: async () => {
    return apiFetch('/api/admin/administrative-units/provinces/');
  },
  createProvince: async (data: { name: string; code?: string; is_active?: boolean }) => {
    return apiFetch('/api/admin/administrative-units/provinces/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  updateProvince: async (id: number, data: { name?: string; code?: string; is_active?: boolean }) => {
    return apiFetch(`/api/admin/administrative-units/provinces/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteProvince: async (id: number) => {
    return apiFetch(`/api/admin/administrative-units/provinces/${id}/`, {
      method: 'DELETE',
    });
  },
  getDistricts: async (province?: number) => {
    const query = province ? `?province=${province}` : '';
    return apiFetch(`/api/admin/administrative-units/districts${query}`);
  },
  createDistrict: async (data: { name: string; code?: string; province: number; is_active?: boolean }) => {
    return apiFetch('/api/admin/administrative-units/districts/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  updateDistrict: async (id: number, data: { name?: string; code?: string; province?: number; is_active?: boolean }) => {
    return apiFetch(`/api/admin/administrative-units/districts/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteDistrict: async (id: number) => {
    return apiFetch(`/api/admin/administrative-units/districts/${id}/`, {
      method: 'DELETE',
    });
  },
  getConstituencies: async (district?: number) => {
    const query = district ? `?district=${district}` : '';
    return apiFetch(`/api/admin/administrative-units/constituencies${query}`);
  },
  createConstituency: async (data: { name: string; code?: string; district: number; is_active?: boolean }) => {
    return apiFetch('/api/admin/administrative-units/constituencies/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  updateConstituency: async (id: number, data: { name?: string; code?: string; district?: number; is_active?: boolean }) => {
    return apiFetch(`/api/admin/administrative-units/constituencies/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteConstituency: async (id: number) => {
    return apiFetch(`/api/admin/administrative-units/constituencies/${id}/`, {
      method: 'DELETE',
    });
  },
  getWards: async (constituency?: number, district?: number) => {
    const params = new URLSearchParams();
    if (constituency) params.append('constituency', String(constituency));
    if (district) params.append('district', String(district));
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiFetch(`/api/admin/administrative-units/wards${query}`);
  },
  createWard: async (data: { name: string; code?: string; constituency: number; is_active?: boolean }) => {
    return apiFetch('/api/admin/administrative-units/wards/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  updateWard: async (id: number, data: { name?: string; code?: string; constituency?: number; is_active?: boolean }) => {
    return apiFetch(`/api/admin/administrative-units/wards/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteWard: async (id: number) => {
    return apiFetch(`/api/admin/administrative-units/wards/${id}/`, {
      method: 'DELETE',
    });
  },

  // Electoral Cycles
  getElectoralCycles: async () => {
    return apiFetch('/api/admin/electoral-cycles/');
  },
  createElectoralCycle: async (data: { name: string; start_year: number; end_year: number; is_active?: boolean; is_current?: boolean }) => {
    return apiFetch('/api/admin/electoral-cycles/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  updateElectoralCycle: async (id: number, data: { name?: string; start_year?: number; end_year?: number; is_active?: boolean; is_current?: boolean }) => {
    return apiFetch(`/api/admin/electoral-cycles/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteElectoralCycle: async (id: number) => {
    return apiFetch(`/api/admin/electoral-cycles/${id}/`, {
      method: 'DELETE',
    });
  },

  // Leadership Positions
  getLeadershipPositions: async (params?: {
    category?: 'parliamentary' | 'party' | 'local_government' | 'all' | string;
    is_active?: boolean;
    search?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '' && value !== 'all') {
          queryParams.append(key, String(value));
        }
      });
    }
    const query = queryParams.toString();
    return apiFetch(`/api/admin/leadership-positions/${query ? `?${query}` : ''}`);
  },
  getLeadershipPositionCategories: async () => {
    return apiFetch('/api/admin/leadership-positions/categories/');
  },
  createLeadershipPosition: async (data: {
    name: string;
    category: 'parliamentary' | 'party' | 'local_government';
    parent?: number;
    term_length?: number;
    appointment_method: string;
    description?: string;
    is_active?: boolean;
    current_holder?: string;
  }) => {
    // Convert to FormData since the backend expects multipart/form-data
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('category', data.category);
    formData.append('appointment_method', data.appointment_method);
    if (data.parent !== undefined) {
      formData.append('parent', String(data.parent));
    }
    if (data.term_length !== undefined) {
      formData.append('term_length', String(data.term_length));
    }
    if (data.description !== undefined) {
      formData.append('description', data.description);
    }
    if (data.is_active !== undefined) {
      formData.append('is_active', String(data.is_active));
    }
    if (data.current_holder !== undefined) {
      formData.append('current_holder', data.current_holder);
    }
    
    return apiFetch('/api/admin/leadership-positions/', {
      method: 'POST',
      body: formData,
    });
  },
  updateLeadershipPosition: async (id: number, data: Partial<{
    name: string;
    category: 'parliamentary' | 'party' | 'local_government';
    parent?: number;
    term_length?: number;
    appointment_method: string;
    description?: string;
    is_active?: boolean;
    current_holder?: string;
  }>) => {
    // Convert to FormData since the backend expects multipart/form-data
    const formData = new FormData();
    if (data.name !== undefined) {
      formData.append('name', data.name);
    }
    if (data.category !== undefined) {
      formData.append('category', data.category);
    }
    if (data.appointment_method !== undefined) {
      formData.append('appointment_method', data.appointment_method);
    }
    if (data.parent !== undefined) {
      formData.append('parent', String(data.parent));
    }
    if (data.term_length !== undefined) {
      formData.append('term_length', String(data.term_length));
    }
    if (data.description !== undefined) {
      formData.append('description', data.description);
    }
    if (data.is_active !== undefined) {
      formData.append('is_active', String(data.is_active));
    }
    if (data.current_holder !== undefined) {
      formData.append('current_holder', data.current_holder);
    }
    
    return apiFetch(`/api/admin/leadership-positions/${id}/`, {
      method: 'PUT',
      body: formData,
    });
  },
  deleteLeadershipPosition: async (id: number) => {
    return apiFetch(`/api/admin/leadership-positions/${id}/`, {
      method: 'DELETE',
    });
  },
};

/**
 * Content API
 */
export const contentApi = {
  /**
   * Get hero section
   */
  getHero: async () => {
    return apiFetch('/api/content/hero/');
  },

  /**
   * Get legislative authority section
   */
  getLegislativeAuthority: async () => {
    return apiFetch('/api/content/legislative-authority/');
  },

  /**
   * Get leader of the day
   */
  getLeaderOfDay: async () => {
    return apiFetch('/api/content/leader-of-day/');
  },

  /**
   * Get navigation links
   */
  getNavigationLinks: async (type?: 'header' | 'footer' | 'all') => {
    const query = type ? `?type=${type}` : '';
    return apiFetch(`/api/content/navigation-links${query}`);
  },

  /**
   * Subscribe to newsletter
   */
  subscribeNewsletter: async (email: string) => {
    return apiFetch('/api/content/newsletter/subscribe/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Get three cards section
   */
  getThreeCards: async () => {
    return apiFetch('/api/content/three-cards/');
  },

  /**
   * Get Parliament page banner
   */
  getParliamentBanner: async () => {
    return apiFetch<{
      id: number;
      cover_image?: string | null;
      cover_image_url?: string | null;
      title: string;
      page_summary?: string | null;
    } | {}>('/api/content/parliament-banner/');
  },

  /**
   * Get Activities page banner
   */
  getActivitiesBanner: async () => {
    return apiFetch<{
      id: number;
      cover_image?: string | null;
      cover_image_url?: string | null;
      title: string;
      page_summary?: string | null;
    } | {}>('/api/content/activities-banner/');
  },

  /**
   * Get All Leaders page banner
   */
  getAllLeadersBanner: async () => {
    return apiFetch<{
      id: number;
      cover_image?: string | null;
      cover_image_url?: string | null;
      title: string;
      page_summary?: string | null;
    } | {}>('/api/content/all-leaders-banner/');
  },

  /**
   * Get President Hero section
   */
  getPresidentHero: async () => {
    return apiFetch<{
      id: number;
      president_name: string;
      president_title: string;
      badge_text: string;
      quote: string;
      background_image?: string | null;
      background_image_url?: string | null;
      portrait_image?: string | null;
      portrait_image_url?: string | null;
    } | {}>('/api/content/president/hero/');
  },

  /**
   * Get President Message section
   */
  getPresidentMessage: async () => {
    return apiFetch<{
      id: number;
      title: string;
      message_text: string;
      image?: string | null;
      image_url?: string | null;
    } | {}>('/api/content/president/message/');
  },

  /**
   * Get President Timeline entries
   */
  getPresidentTimeline: async () => {
    return apiFetch<Array<{
      id: number;
      year: string;
      title: string;
      description: string;
      image?: string | null;
      image_url?: string | null;
      order: number;
    }>>('/api/content/president/timeline/');
  },

  /**
   * Get President Vision section
   */
  getPresidentVision: async () => {
    return apiFetch<{
      id: number;
      quote: string;
      quote_attribution: string;
      background_image?: string | null;
      background_image_url?: string | null;
      values: Array<{
        id: number;
        title: string;
        description: string;
        icon: string;
        order: number;
      }>;
    } | {}>('/api/content/president/vision/');
  },

  /**
   * Get President Governance Areas
   */
  getPresidentGovernanceAreas: async () => {
    return apiFetch<Array<{
      id: number;
      title: string;
      description: string;
      icon: string;
      image?: string | null;
      image_url?: string | null;
      stats?: string | null;
      order: number;
    }>>('/api/content/president/governance-areas/');
  },

  /**
   * Get President Diplomatic Engagements
   */
  getPresidentDiplomaticEngagements: async () => {
    return apiFetch<Array<{
      id: number;
      region: string;
      description: string;
      icon: string;
      image?: string | null;
      image_url?: string | null;
      order: number;
    }>>('/api/content/president/diplomatic-engagements/');
  },

  /**
   * Get President Diplomatic Statistics
   */
  getPresidentDiplomaticStats: async () => {
    return apiFetch<Array<{
      id: number;
      number: string;
      label: string;
      description?: string | null;
      icon: string;
      order: number;
    }>>('/api/content/president/diplomatic-stats/');
  },

  /**
   * Get President Legacy Images
   */
  getPresidentLegacyImages: async () => {
    return apiFetch<Array<{
      id: number;
      image?: string | null;
      image_url?: string | null;
      alt_text?: string | null;
      order: number;
    }>>('/api/content/president/legacy-images/');
  },

  /**
   * Get Home Page Find Representative section
   */
  getHomePageFindRepresentative: async () => {
    return apiFetch<{
      id: number;
      title: string;
      description: string;
      search_placeholder: string;
      service_areas_title: string;
      service_areas_description: string;
      community_focus_title: string;
      community_focus_description: string;
      your_leaders_title: string;
      your_leaders_description: string;
      is_active: boolean;
    } | {}>('/api/content/homepage/find-representative/');
  },

  /**
   * Get Home Page Explore Representatives section
   */
  getHomePageExploreRepresentatives: async () => {
    return apiFetch<{
      id: number;
      title: string;
      subtitle: string;
      description: string;
      background_image?: string | null;
      background_image_url?: string | null;
      is_active: boolean;
    } | {}>('/api/content/homepage/explore-representatives/');
  },

  /**
   * Get Home Page Newsletter section
   */
  getHomePageNewsletter: async () => {
    return apiFetch<{
      id: number;
      title: string;
      description: string;
      email_placeholder: string;
      is_active: boolean;
    } | {}>('/api/content/homepage/newsletter/');
  },

  /**
   * Get Home Page Video section
   */
  getHomePageVideo: async () => {
    return apiFetch<{
      id: number;
      title: string;
      subtitle: string;
      description: string;
      background_image?: string | null;
      background_image_url?: string | null;
      video_url?: string | null;
      is_active: boolean;
    } | {}>('/api/content/homepage/video/');
  },

  /**
   * Get Home Page Your Parliament section
   */
  getHomePageYourParliament: async () => {
    return apiFetch<{
      id: number;
      section_title: string;
      section_description: string;
      items: Array<{
        id: number;
        title: string;
        description: string;
        icon: string;
        order: number;
      }>;
      is_active: boolean;
    } | {}>('/api/content/homepage/your-parliament/');
  },

  /**
   * Get Contact page content (FAQ & Contact)
   */
  getContactPage: async () => {
    return apiFetch<{
      id: number;
      breadcrumb: string;
      hero_title: string;
      hero_description: string;
      faq_title: string;
      faq_subtitle?: string | null;
      contact_title: string;
      contact_intro?: string | null;
      contact_email?: string | null;
      contact_phone?: string | null;
      contact_address?: string | null;
      organizer_title: string;
      organizer_body?: string | null;
      conference_office_title: string;
      conference_office_body?: string | null;
      data_protection_title: string;
      data_protection_text: string;
      privacy_policy_url?: string | null;
      faqs: Array<{ id: number; question: string; answer: string; order: number }>;
    } | {}>('/api/content/contact/');
  },

  submitContact: async (payload: { first_name: string; last_name: string; email: string; message: string; company?: string }) => {
    return apiFetch<{ ok: boolean }>('/api/content/contact/submit/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getLegalInformation: async () => {
    return apiFetch<
      | {
          id: number;
          breadcrumb: string;
          hero_title: string;
          hero_description?: string | null;
          section_privacy_policy_title: string;
          privacy_policy_body?: string | null;
          section_terms_conditions_title: string;
          terms_conditions_body?: string | null;
          section_cookie_policy_title: string;
          cookie_policy_body?: string | null;
          section_accessibility_title: string;
          accessibility_body?: string | null;
          section_site_map_title: string;
          site_map_body?: string | null;
          section_disclaimer_title: string;
          disclaimer_body?: string | null;
          updated_at: string;
        }
      | {}
    >('/api/content/legal/');
  },
};

/**
 * Notifications API (navbar bell)
 */
export const notificationsApi = {
  list: async (sessionType: 'user' | 'admin' = 'user', limit: number = 50) => {
    const params = new URLSearchParams();
    params.set('session_type', sessionType);
    params.set('limit', String(limit));
    return apiFetch<{ notifications: Array<{ id: number; title: string; message: string; link?: string | null; is_read: boolean; created_at: string }> }>(
      `/api/notifications/?${params.toString()}`
    );
  },
  markRead: async (id: string | number, sessionType: 'user' | 'admin' = 'user') => {
    const params = new URLSearchParams();
    params.set('session_type', sessionType);
    return apiFetch<{ ok: boolean }>(`/api/notifications/${id}/read/?${params.toString()}`, {
      method: 'POST',
    });
  },
  markAllRead: async (sessionType: 'user' | 'admin' = 'user') => {
    const params = new URLSearchParams();
    params.set('session_type', sessionType);
    return apiFetch<{ ok: boolean }>(`/api/notifications/read-all/?${params.toString()}`, {
      method: 'POST',
    });
  },
  delete: async (id: string | number, sessionType: 'user' | 'admin' = 'user') => {
    const params = new URLSearchParams();
    params.set('session_type', sessionType);
    return apiFetch<{ ok: boolean }>(`/api/notifications/${id}/?${params.toString()}`, {
      method: 'DELETE',
    });
  },
};

/**
 * News API
 */
export const newsApi = {
  /**
   * Get news data (banner and articles) with optional pagination
   * @param limit - Number of articles per page (default: all)
   * @param page - Page number (default: 1)
   */
  getNewsData: async (limit?: number, page: number = 1) => {
    const params = new URLSearchParams();
    if (limit) {
      params.append('limit', limit.toString());
      params.append('page', page.toString());
    }
    const queryString = params.toString();
    const url = queryString ? `/api/news/?${queryString}` : '/api/news/';
    
    return apiFetch<{
      banner: {
        id: number;
        cover_image?: string | null;
        title: string;
        page_summary: string;
      } | null;
      news_articles: Array<{
        id: number;
        title: string;
        body?: string | null;
        excerpt?: string | null;
        slug: string;
        category?: string;
        date: string;
        image?: string | null;
        is_featured: boolean;
      }>;
      total_count: number;
      page?: number;
      total_pages?: number;
      limit?: number;
    }>(url);
  },

  getNewsCategories: async () => {
    return apiFetch<Array<{ id: number; name: string; slug: string }>>('/api/news/categories/');
  },

  /**
   * Get news article by slug
   */
  getNewsBySlug: async (slug: string) => {
    return apiFetch<{
      id: number;
      title: string;
      slug: string;
      body?: string | null;
      excerpt?: string | null;
      category?: string;
      date: string;
      image?: string | null;
      image_caption?: string | null;
      quote?: string | null;
      is_featured: boolean;
    }>(`/api/news/${slug}/`);
  },

  /**
   * Leader news (dashboard-created). These appear on the leader profile and also in the general news feed.
   */
  getLeaderNews: async (leaderProfileId: number) => {
    return apiFetch<
      Array<{
        id: number;
        title: string;
        body?: string | null;
        excerpt?: string | null;
        slug: string;
        category?: string;
        category_id?: number | null;
        date: string;
        date_iso?: string | null;
        image?: string | null;
        image_caption?: string | null;
        quote?: string | null;
        is_featured: boolean;
        leader_profile_id?: number | null;
        leader_profile_name?: string | null;
      }>
    >(`/api/leaders/${leaderProfileId}/news/`);
  },

  createLeaderNews: async (leaderProfileId: number, data: FormData) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/news/`, {
      method: 'POST',
      body: data,
    });
  },

  updateLeaderNews: async (leaderProfileId: number, id: number, data: FormData) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/news/${id}/`, {
      method: 'PUT',
      body: data,
    });
  },

  deleteLeaderNews: async (leaderProfileId: number, id: number) => {
    return apiFetch(`/api/leaders/${leaderProfileId}/news/${id}/`, {
      method: 'DELETE',
    });
  },
};

export { ApiError, API_BASE_URL };

